import { useMemo } from 'react';
import { useHousehold } from '@kidshome/api-contract';
import { useAuthStore } from '@/entities/session';

/**
 * Household formatting context (ADR-0023/0025). Every money/points/date render in the family app
 * resolves locale + timezone + currency from here, then formats via the `@kidshome/i18n` `Intl`
 * wrappers — never `new Date()`/`toLocaleString` inline, never a currency string in a catalog.
 *
 * - `locale`   — the member's `User.locale` (mirrored to i18n at login), falling back to `en`.
 * - `timeZone` — the household timezone from `GET /v1/household` (ADR-0023; every member incl. a
 *   child holds `member:read`). The device IANA zone is a fallback ONLY while the cached read is in
 *   flight / errored, so day boundaries don't briefly wander before the server value lands.
 * - `currency` — the household ISO currency for *real money* (cash/savings) from the same read, so a
 *   non-USD household sees correct aggregate balances. Per-row ledger entries still carry their own
 *   authoritative `currency`; this default covers aggregates/totals.
 * - `name`     — the household display name (handy for headers), `null` until the read resolves.
 * - `ownerUserId` — the household OWNER's user id (`null` until the read resolves). Lets the client
 *   gate owner-only affordances (e.g. account deletion) up front instead of walking a non-owner
 *   admin into a guaranteed server 403; the server stays the real enforcement.
 *
 * The household read is cached with a long `staleTime` (formatting config rarely changes within a
 * session) so this hook adds no per-render network cost; every call site already funnels through it.
 */
export interface HouseholdContext {
  readonly locale: string;
  readonly timeZone: string;
  readonly currency: string;
  readonly name: string | null;
  readonly ownerUserId: string | null;
}

/** Safe defaults used ONLY while the household read is loading or has errored. */
export const DEFAULT_CURRENCY = 'USD';

function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function useHouseholdContext(): HouseholdContext {
  const locale = useAuthStore((s) => s.user?.locale);
  // Household formatting config changes rarely within a session; keep it fresh for an hour and cached
  // for the day so this hook adds no per-render network cost. Every member (incl. children) may read
  // it (`member:read`), so the kid path resolves the real currency/timezone too.
  const { data } = useHousehold({
    staleTime: 60 * 60_000,
    gcTime: 24 * 60 * 60_000,
  });

  return useMemo(
    () => ({
      locale: locale ?? 'en',
      // Prefer the server values; fall back to a sensible device default until the read resolves.
      timeZone: data?.timezone || deviceTimeZone(),
      currency: data?.currency || DEFAULT_CURRENCY,
      name: data?.name ?? null,
      ownerUserId: data?.ownerUserId ?? null,
    }),
    [locale, data?.timezone, data?.currency, data?.name, data?.ownerUserId],
  );
}
